// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Flex, Link, SwitchField, Typography } from '@iheartjane/reefer';
import { formatCurrency, pluralize } from '@iheartjane/utils';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import { cartSize } from '@jane/shared-ecomm/util';
import type { StoreSpecial } from '@jane/shared/types';

import type { CheckoutFormFieldsDevice } from '../../components';
import {
  BaseCustomerForm,
  BirthDateField,
  CartCheckoutError,
  FirstNameField,
  LastInitialField,
  LastNameField,
  NotesField,
  PhoneNumberField,
  SubmitCustomerInfoButton,
} from '../../components';
import type { CreateCheckoutParams, RootLoaderType } from '../../data-access';
import { useGetCart, useSubmitOrder } from '../../data-access';
import { useTypedRouteLoaderData } from '../../hooks';
import {
  useCartInfoForTracking,
  useProductTracking,
  useSessionContext,
} from '../../providers';
import type { CheckoutResponse } from '../../types';
import { formatBirthdate, trackOrderSubmitted } from '../../util';

const HeaderBox = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.grays.white,
  border: '1px solid rgba(0, 0, 0, 0.05)',
  borderRadius: '12px',
  boxShadow: '0px 2px 16px rgba(0, 0, 0, 0.05)',
}));

export const CheckoutFormOnDevice = () => {
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const { kiosk, storeId, store } =
    useTypedRouteLoaderData<RootLoaderType>('root');

  const [noteShown, setNoteShown] = useState(false);
  const [anonymousCheckout, setAnonymousCheckout] = useState(
    store.kiosk_anonymous_checkout === 'required'
  );
  const {
    cartToppers: { cartTopperIds },
    inlineAds: { inlineAdIds },
  } = useProductTracking();

  const { name: storeName } = store;
  const { data: cart = {}, isLoading: isCartLoading, isSuccess } = useGetCart();

  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const { setCartInfo } = useCartInfoForTracking();
  const cartItemsCount = useMemo(() => cartSize(cart), [cart]);

  const { jdid } = useSessionContext();

  const allowAnonymousCheckout = store.kiosk_anonymous_checkout === 'allowed';
  const strictAnonymousCheckout = store.kiosk_strict_anonymous_checkout;

  const onAddNote = useCallback(() => {
    setNoteShown(true);
  }, []);

  const dmTypesInCart = useMemo(() => {
    const dmTypes = [];

    if (cartTopperIds.length > 0) {
      dmTypes.push('bag');
    }

    if (inlineAdIds.length > 0) {
      dmTypes.push('in-line');
    }

    return dmTypes;
  }, [cartTopperIds, inlineAdIds]);

  const onOrderError = useCallback(
    (error: unknown, params: CreateCheckoutParams) => {
      trackOrderSubmitted({
        cart,
        kiosk,
        specials,
        store,
        trackingBag: {
          birthDate: params.birth_date,
          checkoutError: `${error}`,
          message: params.message || null,
          phoneNumber:
            anonymousCheckout || kiosk?.disable_phone_number_at_checkout
              ? null
              : params.phone,
        },
        dmType: dmTypesInCart.length === 0 ? null : dmTypesInCart,
        paymentMethod: null,
      });

      setError(String(error));
    },
    [anonymousCheckout, cart, kiosk, specials, store, dmTypesInCart]
  );

  const onOrderSuccess = useCallback(
    async (
      { reservation: { id, queue_number } }: CheckoutResponse,
      { birth_date, message, phone }: CreateCheckoutParams
    ) => {
      trackOrderSubmitted({
        cart,
        kiosk,
        specials,
        store,
        trackingBag: {
          birthDate: birth_date,
          checkoutError: null,
          message: message || null,
          phoneNumber:
            anonymousCheckout || kiosk?.disable_phone_number_at_checkout
              ? null
              : phone,
        },
        dmType: dmTypesInCart.length === 0 ? null : dmTypesInCart,
        paymentMethod: null,
      });
      setCartInfo({
        cart,
        birthDate: birth_date,
        phoneNumber:
          anonymousCheckout || kiosk?.disable_phone_number_at_checkout
            ? null
            : phone,
        selectedBankAccount: null,
        signedInOrder: false,
      });

      //storing the queue number in local storage to ensure that it persists on the Order Checkout page
      localStorage.setItem('queueNumber', queue_number);

      navigate(`/stores/${storeId}/mobile/reservation/${id}/order-complete`, {
        replace: true,
      });
    },
    [
      cart,
      kiosk,
      specials,
      store,
      anonymousCheckout,
      dmTypesInCart,
      setCartInfo,
      navigate,
      storeId,
    ]
  );

  const { mutate: submitOrder, isLoading: isSubmitting } = useSubmitOrder({
    onError: onOrderError,
    onSuccess: onOrderSuccess,
  });

  const formMethods = useForm<CheckoutFormFieldsDevice>({
    mode: 'onBlur',
  });

  const validateAndSubmit = async () => {
    const isValid = await formMethods.trigger();

    if (isValid) {
      handleSubmit();
    }
  };

  const handleSubmit = useCallback(async () => {
    const [message, phone, firstName, lastName, lastInitial, birthDate] =
      formMethods.getValues([
        'checkout-notes',
        'contact-num',
        'name-f',
        'name-l',
        'name-l',
        'b-day',
      ]);

    submitOrder({
      birth_date: birthDate ? formatBirthdate(birthDate) : undefined,
      cart_uuid: cart.uuid,
      current_url: window.location.href,
      j_device_id: jdid,
      message,
      name:
        strictAnonymousCheckout && anonymousCheckout
          ? 'ANONYMOUS CHECKOUT'
          : [firstName, lastName || lastInitial].join(' '),
      phone:
        anonymousCheckout || kiosk?.disable_phone_number_at_checkout
          ? ''
          : phone,
      products: cart.products,
      store_id: storeId,
    });
  }, [
    anonymousCheckout,
    cart.products,
    cart.uuid,
    jdid,
    kiosk,
    storeId,
    strictAnonymousCheckout,
    submitOrder,
    formMethods,
  ]);

  return (
    <>
      <Flex width="100%">
        <HeaderBox width="100%" flexDirection="column" m={16} p={16}>
          <Typography mb={8} variant="body" color="grays-mid">
            Kiosk order
          </Typography>
          <Typography variant="header-bold">{storeName}</Typography>
          {isSuccess && (
            <Typography variant="header">
              {pluralize({ noun: 'Item', number: cartItemsCount })} (
              {formatCurrency(cart.order_total)})
            </Typography>
          )}
        </HeaderBox>
      </Flex>
      <Flex flexDirection="column" px={24} pb={24} height="100%" width="100%">
        <BaseCustomerForm
          formMethods={formMethods}
          handleSubmit={validateAndSubmit}
        >
          <Typography variant="title-bold">Checkout</Typography>
          <Flex pt={8}>
            <Typography variant="body">
              {strictAnonymousCheckout && anonymousCheckout
                ? `${
                    kiosk?.show_checkout_message_field
                      ? 'Add a note or click'
                      : 'Click'
                  } to finalize your order.`
                : 'Some personal info needs to be collected in order for you to receive this order.'}
            </Typography>
          </Flex>
          {(!anonymousCheckout || !strictAnonymousCheckout) && (
            <>
              <Flex flexDirection="column" pt={16}>
                <Flex pb={4}>
                  <Typography variant="body-bold">First name</Typography>
                </Flex>
                <FirstNameField autoComplete="given-name" labelHidden />
              </Flex>
              <Flex flexDirection="column" pt={16}>
                <Flex pb={4}>
                  <Typography variant="body-bold">
                    Last {anonymousCheckout ? 'initial' : 'name'}
                  </Typography>
                </Flex>
                {anonymousCheckout ? (
                  <LastInitialField autoComplete="family-name" labelHidden />
                ) : (
                  <LastNameField autoComplete="family-name" labelHidden />
                )}
              </Flex>
              {!anonymousCheckout &&
                !kiosk?.disable_phone_number_at_checkout && (
                  <Flex flexDirection="column" pt={16}>
                    <Flex pb={4}>
                      <Typography variant="body-bold">Phone number</Typography>
                    </Flex>
                    <PhoneNumberField autoComplete="tel" labelHidden />
                  </Flex>
                )}
              {!anonymousCheckout && !kiosk?.disable_birth_date_at_checkout && (
                <Flex flexDirection="column" pt={16}>
                  <Flex pb={4}>
                    <Typography variant="body-bold">Birth date</Typography>
                  </Flex>
                  <BirthDateField
                    autoComplete="bday"
                    labelHidden
                    store={store}
                  />
                </Flex>
              )}
            </>
          )}

          {allowAnonymousCheckout && (
            <Flex flexDirection="row" pt={24}>
              <SwitchField
                label="Check out anonymously"
                name="anonymous checkout toggle"
                onChange={setAnonymousCheckout}
              />
            </Flex>
          )}

          {kiosk?.show_checkout_message_field && (
            <Flex pt={16}>
              {!noteShown && (
                <Link
                  color="grays-black"
                  ariaLabel="show note field"
                  onClick={onAddNote}
                >
                  <Typography variant="body">Add a note</Typography>
                </Link>
              )}
              {noteShown && <NotesField />}
            </Flex>
          )}

          <Flex pt={24}>
            <SubmitCustomerInfoButton
              disabled={isCartLoading}
              loading={isCartLoading || isSubmitting}
              size="default"
              onClick={() => validateAndSubmit()}
            />
          </Flex>
        </BaseCustomerForm>
      </Flex>
      {error && (
        <CartCheckoutError error={error} onClose={() => setError('')} />
      )}
    </>
  );
};
